/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode, ReactElement } from "react";
import { BillingType, ClaimServiceProvidedFor, ClaimStatus, UsesPerYearDefinition, DocumentCategory } from "./consts";
import { ChecklistType, ClaimPerson } from "./api/protos/messages/claims.message_pb";
import { StatusCode } from "grpc-web";
import { State } from "./api/protos/messages/core.message_pb";

export type KVP = {
	[key: string]: string | number;
};

export enum SortDirection {
	Asc = "asc",
	Desc = "desc"
}

export enum ClaimServiceBillingType {
	Flat = "flat",
	FlatHigh = "flat-high",
	HourlyAttorney = "hourly-attorney",
	HourlyParalegal = "hourly-paralegal"
}

export interface DataTableColumn {
	header: string;
	id: string;
	key?: string;
	/** Is this column filterable? */
	filterable: boolean;
	/** A set width for this column */
	width?: string;
	/** A min width for this column */
	minWidth?: string;
	/** A max width for this column */
	maxWidth?: string;
	/** Is this column hidden? */
	isHidden: boolean;
	/** Use this column for unique id selection? */
	isSelectable?: boolean;
	/** Automatically try to link this column? */
	linkable?: boolean;
	/** Is this column sortable? */
	isSortable: boolean;
	/** Is this column a date? */
	isDate?: boolean;
	/** Is this column a date with time? */
	isDateTime?: boolean;
	/** Expands the row for extra data */
	isExpandingRow?: boolean;
	/** Should this column text be truncated and not wrap? */
	truncate?: boolean;
	/** Should this column be given the entire row data? This is useful for the valueMap() function */
	allData?: boolean; // Give column all row data
	/** A custom value mapping function to return any custom html you want */
	valueMap?: (input: any) => ReactNode;
	/** A custom filter for the column */
	customFilter?: CustomColumnFilter;
}

export interface CustomColumnFilter {
	type: CustomColumnFilterType;
	options?: Array<CustomColumnFilterOption>;
}

export interface CustomColumnFilterOption {
	label: string;
	value: string | number;
	icon?: ReactElement;
}

export enum CustomColumnFilterType {
	Select,
	MultiSelect,
	DatePicker
}

export interface PaginationResponse {
	total: number;
	perPage: number;
	current_page: number;
	last_page: number;
	first_page: number;
}

export interface PaginatedApiResponse {
	data: Array<KVP>;
	meta: PaginationResponse;
}

/**
 * Used for <DetailField type="chakra-select" />
 */
export interface ChakraSelectOption {
	label: string;
	value: string;
	// options?: figure this out later
}

/**
 * Used for <DetailField type="select" />
 */
export interface SelectOption {
	// Resolves down to:
	// [Value]: DisplayLabel
	[id: string]: string;
}

/**
 * Used for <DetailField type="chakra-select" />
 */
export interface GroupSelectOption {
	label: string;
	value: string;
	options: Array<ChakraSelectOption>;
}

export interface User {
	id: number;
	username: string;
	email: string;
	name: string;
	role: string;
	externalId: string;
}

export interface UpsertUser {
	id: number;
	username?: string;
	email?: string;
	name?: string;
	externalId?: string;
	password?: string;
}

export interface RefreshToken {
	expires: number;
	issuedAt: number;
	token: string;
}

export interface JwtToken {
	email: string;
	exp: number;
	iat: number;
	iss: string;
	sub: string;
	token: string;
}

export interface ClaimsCookies {
	user: User;
	jwtToken: JwtToken;
	refreshToken: RefreshToken;
}

export interface FileIdentifier {
	fileName: string;
	fileId: string;
	fileSize: number;
}

export interface ClaimDocument {
	file: FileIdentifier;
	category: DocumentCategory;
	isDeleted?: boolean;
	fileSize?: number;
	isGenerated?: boolean;
}

export interface ChecklistItem {
	id: number | undefined;
	claimId: number;
	type: ChecklistType;
	label: string;
	value: boolean;
}

export interface Claim {
	claimId?: number;
	caseId?: number;
	lampCaseId?: number;
	atty?: ClaimPerson;
	attyActive?: boolean; // attorney is signed up for claims (active Provider account); internal display only
	member?: ClaimPerson;
	dateOfInitialConsultation: Date;
	finalDateOfService: Date;
	assignedAnalyst?: ClaimPerson;
	claimServices: Array<ClaimService>;
	documents: Array<ClaimDocument>;
	otherNonCoveredFees?: string;
	otherNonCoveredHours?: string;
	billingAdditionalFees?: string;
	billingtotal?: string;
	status: ClaimStatus;
	checklistItems?: ChecklistItem[];
	checklistComplete?: boolean;
	cancelReason?: string;
	denyReason?: string;
	dueDate?: Date;
	cleanDate?: Date;
	submittedAt?: Date;
	approvedAt?: Date;
	receivedOn?: Date;
	legalMatterState?: State;
	isHeldForAudit?: boolean;
}

export interface ClaimReport {
	id: number;
	claimId: number;
	reportName: string;
	reportedOn: string;
	type: "Positive" | "Negative";
	createdAt?: number;
}

export interface ClaimReportImportError {
	rowNumber: number;
	claimId: number;
	reportName: string;
	reportedOn: string;
	type: string;
	errorMessage: string;
}

// export interface ClaimServiceBillingRate {
// 	discountedRate: string;
// 	standardRate: string;
// 	hours?: string;
// }

// export interface ClaimServiceBillingHourly {
// 	attorney?: ClaimServiceBillingRate;
// 	paralegal?: ClaimServiceBillingRate;
// }

export interface ClaimServiceBilling {
	id?: number;
	claimId?: number;
	serviceId?: number;
	discountedRate?: string;
	standardRate?: string;
	totalAmount?: string;
	hours?: string;
	type?:
		| ClaimServiceBillingType.Flat
		| ClaimServiceBillingType.FlatHigh
		| ClaimServiceBillingType.HourlyAttorney
		| ClaimServiceBillingType.HourlyParalegal;
}

export interface ClaimService {
	legalBenefitId?: number;
	serviceProvidedFor?: ClaimServiceProvidedFor;
	billing?: ClaimServiceBilling[];
	serviceId?: number;
	claimId?: number;
	recipientAge?: number;
	// New High Fixed Value case completion fields
	finalDisposition?: boolean;
	consultationCompleted?: boolean;
	officeWorkCompleted?: boolean;
	negotiationCorrespondenceCompleted?: boolean;
	lawsuitCaseFiledRespondedCompleted?: boolean;
	courtAppearanceCompleted?: boolean;
	caseFullyCompleted?: boolean;
}

// It seems that this won't be directly used but we'll abuse polymorphism on the specific
// legal benefit to apply the "oneOf" rule
export interface LegalBenefit {
	id: number;
	benefitTitle: string;
	matterBillingCode: string;
	matterBenefitDescription: string;
	matterPaymentParameters: string;
	// caps
	hourlyRateCap?: string;
	totalHoursCap?: string;
	totalAmountCap?: string;
	raterMaxRateCap?: string;

	matterLimitations: string;
	paidInFull: boolean;
	usesPerYear?: number;
	usesPerYearDefinition?: UsesPerYearDefinition;
	matterSpecialInstructions: string;
	providerInstructions?: string;
	// grouping
	benefitGroupingId?: number;
	benefitGroupingTitle?: string;
	// coverage
	coversPrimaryMember?: boolean;
	requireSupportingDocument?: boolean;
	coversSpouse?: boolean;
	coversDomesticPartner?: boolean;
	coversDependantChild?: boolean;
	coversElderParent?: boolean;

	billingType?: BillingType;
	lgPolicyId?: number;
	hasOverride?: boolean;
	// Provider's standard rates (prepopulated from provider profile)
	providerStandardHourlyRate?: string;
	providerStandardFlatRate?: string;
}

export type RecipientOptions = {
	[key in ClaimServiceProvidedFor]?: boolean;
};

export interface DisplayCardStatementProps {
	data: {
		dates: {
			dateOfInitialConsultation: Date;
			finalDateOfService: Date;
		};
		billingItems: {
			service: string;
			recipient: string;
			providerType: string;
			hours: any;
			networkRate: string;
			standardRate: string;
			totalAmount: string;
			isHighFixedValue?: boolean;
			selectedCaseWorkLevels?: string[];
			tooltipData?: {
				highestWorkLevel: string;
				networkMaximum: string;
				percentage: number;
				calculatedTotal: string;
			};
		}[];
		summary: {
			otherNonCoveredFees: string;
			otherNonCoveredHours: string;
			billingAdditionalFees: string;
			billingTotal: string;
			totalHours: string;
			totalNetworkCost: string;
			totalStandardCost: string;
			claimAndNonCoveredItems: string;
			marketValue: string;
		};
		hasHighFixedValueServices?: boolean;
	};
}

export interface NotesActivitiesDisplay {
	id: number;
	claimId: number;
	userId: number;
	userName: string;
	userRole: string;
	type?: { noteType?: string; label?: string; color?: string };
	message: string;
	isPinned?: boolean;
	createdAt: number;
}

export interface ErrorResponse {
	errorCode: string;
	errorMessage: string;
}

export interface DocumentOperationResult {
	success?: boolean;
	error?: {
		code?: StatusCode;
		message: string;
	};
}

export interface RemoveDocumentResult extends DocumentOperationResult {}

export interface RecoverDocumentResult extends DocumentOperationResult {}

export interface PermanentlyRemoveDocumentResult extends DocumentOperationResult {}

/**
 * Claim Reservation Types
 */
export interface ClaimReservation {
	id: number;
	userId: number;
	status: string;
	passGroupId?: string;
	legalFilingState?: string;
	benefitCode?: string;
	createdByUserId: number;
	createdOn: number;
	removedByUserId?: number;
	removedOn?: number;
	isDeleted: boolean;
	user?: ClaimPerson.AsObject;
	createdByUser?: ClaimPerson.AsObject;
	removedByUser?: ClaimPerson.AsObject;
}

export interface CreateReservationFormData {
	userId: number;
	passGroupId?: string;
	benefitCode?: string;
	legalFilingState?: string;
}

export interface ListReservationsFilters {
	page: number;
	limit: number;
	sortField?: string;
	sortDir?: string;
	filterUserId?: number;
	filterPassGroupId?: string;
	includeDeleted?: boolean;
}

export enum ReservationStatus {
	Active = "active",
	Inactive = "inactive"
}
